/**
 * The workspace navigation's rules — Louis's top bar, promoted from Home to
 * Flow by default and every other employee route behind the `workspace-nav` flag.
 *
 * Framework-free on purpose, like `appearance-paper.ts` beside the profile
 * panel: these are the parts of the navigation with a wrong answer (which nav
 * a route gets, which item is current, which paper the theme toggle lands on),
 * so they are testable without a TestBed or a shell.
 *
 * USED BY: AppShellComponent (which nav to draw, the current item, the model
 * it hands the nav) and WorkspaceNavComponent (the theme toggle, analytics).
 */
import type { ThemeName } from '@ricoh-paper/angular';

import { THEME_GROUP_OPTIONS } from '../../../../core/constants/theme-options';
import { sectionWorkspaceNav } from '../../../../core/constants/navigation';
import type { AppSection } from '../../../../core/services/shell-state.service';
import { paperForMode, type LastSeenPapers } from '../profile-panel/appearance-paper';

/** The PostHog flag that draws Louis's navigation on every employee route. Default off. */
export const WORKSPACE_NAV_FLAG = 'workspace-nav';

/** The nav item that stands for Ask, beside the workspaces. */
export const ASK_NAV_ID = 'ask';

/**
 * Where the nav sits on a route.
 *
 *   page     — above a page that seats itself below the nav (`--shell-sticky`).
 *   floating — over a full-bleed canvas (the map, the globe), as glass.
 */
export type WorkspaceNavPlacement = 'page' | 'floating';

/** What the shell knows when it decides which navigation to draw. */
export interface WorkspaceNavRouteFacts {
  /** The route carries `data.homeWorkspace`: `/home`, `/welcome` and the Home harnesses. */
  readonly homeWorkspaceRoute: boolean;
  /** The `workspace-nav` flag, after the review override. */
  readonly flagOn: boolean;
  /** The section the URL resolves to. */
  readonly section: AppSection;
  /** Inside the admin route tree (`/settings/*`), which keeps its own chrome. */
  readonly adminScope: boolean;
  /** A route that draws its whole chrome itself (`CHROME_TAKEOVERS`). */
  readonly chromeTakeover: boolean;
}

/**
 * Where the workspace nav sits on this route, or `null` when the shell keeps
 * what it had (the Paper bar, or nothing on a takeover).
 *
 * Home and Flow draw the shared navigation whatever the rollout flag says.
 * Flow includes its Projects alias and every nested route (including Knowledge).
 * Everywhere else the flag decides, and the
 * section's own `SECTION_CHROME.workspaceNav` says how — so the shell holds no
 * list of sections, exactly as for the bar's other chrome decisions.
 */
export function workspaceNavPlacement(facts: WorkspaceNavRouteFacts): WorkspaceNavPlacement | null {
  if (facts.homeWorkspaceRoute) return 'page';
  if (facts.adminScope || facts.chromeTakeover) return null;
  if (!facts.flagOn && !workspaceNavIsStandard(facts.homeWorkspaceRoute, facts.section)) return null;
  const presence = sectionWorkspaceNav(facts.section);
  return presence === 'none' ? null : presence;
}

/** Home and Flow have adopted the shared navigation independently of the rollout flag. */
export function workspaceNavIsStandard(homeWorkspaceRoute: boolean, section: AppSection): boolean {
  return homeWorkspaceRoute || section === 'flow' || section === 'projects';
}

/**
 * Home d0bc222: wait for the real route and, only where it decides, the flag.
 * This prevents the legacy bar flashing or losing an open menu during cold load.
 */
export function shellChromeSettled(facts: { routed: boolean; standardWorkspaceNav: boolean; flagKnown: boolean }): boolean {
  return facts.routed && (facts.standardWorkspaceNav || facts.flagKnown);
}

/**
 * The nav's current item for a section.
 *
 * Ask is current while it is open from the side (`askCurrent`) and on `/ai`,
 * where it is the page. Personal settings has no workspace of its own and
 * borrows Settings', as the Paper bar always has; the Projects alias is Flow.
 * Any other section is its own workspace id — and a section with no chip
 * (the app viewer) simply marks nothing.
 */
export function workspaceNavActiveId(section: AppSection, askCurrent: boolean): string {
  if (askCurrent || section === 'ai-chat') return ASK_NAV_ID;
  if (section === 'profile') return 'settings';
  if (section === 'projects') return 'flow';
  return section;
}

/**
 * `?workspaceNav=1` (or `0`) on the page's own query string turns the nav on
 * (or off) for review without PostHog — dev builds only, and read off
 * `location.search` like `?testHarness=1`, so a hash route cannot set it.
 * `null` when there is no override and the flag decides.
 */
export function workspaceNavReviewOverride(search: string, production: boolean): boolean | null {
  if (production) return null;
  const value = new URLSearchParams(search).get('workspaceNav');
  if (value === '1') return true;
  if (value === '0') return false;
  return null;
}

// ── The theme toggle ─────────────────────────────────────────────────────

/**
 * The paper the sun/moon toggle lands on.
 *
 * A two-way toggle over a three-way choice (Light, Graphite, Black): it must
 * never discard the dark the viewer chose. Light goes to the dark paper last
 * seen (Black for somebody who picked Black), falling back to Graphite, the
 * default dark; dark goes back to the light paper last seen, falling back to
 * Workplace paper. High contrast stays inside its own pair. The precedence is
 * the profile panel's (`paperForMode`), so the two controls cannot disagree.
 */
export function themeToggleTarget(
  current: ThemeName,
  isDark: boolean,
  remembered: LastSeenPapers,
): ThemeName {
  return paperForMode(current, isDark ? 'light' : 'dark', remembered);
}

/** `remembered` with `theme` noted as the paper last seen at its luminance. */
export function rememberPaper(
  remembered: LastSeenPapers,
  theme: ThemeName,
  isDark: boolean,
): LastSeenPapers {
  return isDark ? { ...remembered, dark: theme } : { ...remembered, light: theme };
}

/** Where the remembered papers persist, so Black survives a reload spent on Light. */
export const REMEMBERED_PAPERS_KEY = 'im-theme-remembered';

const KNOWN_PAPERS: ReadonlySet<string> = new Set(THEME_GROUP_OPTIONS.map(option => option.name));

/** The remembered papers in `raw` (the stored JSON), keeping only papers the catalogue still offers. */
export function parseRememberedPapers(raw: string | null): LastSeenPapers {
  const empty: LastSeenPapers = { light: null, dark: null };
  if (!raw) return empty;
  try {
    const value = JSON.parse(raw) as Partial<Record<'light' | 'dark', unknown>>;
    const pick = (paper: unknown): ThemeName | null =>
      typeof paper === 'string' && KNOWN_PAPERS.has(paper) ? (paper as ThemeName) : null;
    return { light: pick(value?.light), dark: pick(value?.dark) };
  } catch {
    // A hand-edited or truncated value: nothing remembered rather than a throw in the nav.
    return empty;
  }
}

// ── Analytics ────────────────────────────────────────────────────────────

/** Ids in a path (Mongo ObjectIds, UUIDs, long numbers) collapse to `:id`. */
const ID_SEGMENT = /^(?:[0-9a-f]{24}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d{4,})$/i;

/**
 * The route a nav event happened on: the path without query or fragment, ids
 * collapsed, so `/people/<id>?tab=x` reads `/people/:id`. No entity id ever
 * reaches the payload through it.
 */
export function navRouteForAnalytics(url: string): string {
  const path = url.split('#')[0].split('?')[0] || '/';
  return path
    .split('/')
    .map(segment => (ID_SEGMENT.test(segment) ? ':id' : segment))
    .join('/');
}

/** The nav's two layouts: Louis's full bar, and the phone bar below 768px (`--bp-narrow`). */
export type WorkspaceNavLayout = 'wide' | 'compact';

/** The media query that switches to the compact layout. Matches the rail's CSS. */
export const COMPACT_NAV_QUERY = '(max-width: 768px)';

/**
 * Below this the rail hides every chip but the current one (the 540px block in
 * `soft-frost-rail.component.scss`), so the app menu has to list the core apps
 * again. Keep the two widths together.
 */
export const RAIL_CHIPS_HIDDEN_QUERY = '(max-width: 540px)';

// ── Icons ────────────────────────────────────────────────────────────────

/**
 * Each workspace's glyph in the nav, from the nav's own stroke set
 * (`im-preview-icon`). Louis chose the first four; the rest follow the
 * registry's meaning in the same set. A spec walks `NAV_WORKSPACES` against
 * this, so a new workspace cannot reach the nav as a blank dash.
 */
export const WORKSPACE_NAV_ICONS: Readonly<Record<string, string>> = {
  home: 'home',
  help: 'book-open',
  map: 'map',
  discover: 'network',
  schedule: 'calendar',
  flow: 'flow',
  projects: 'flow',
  visitors: 'people',
  insights: 'chart-column',
  content: 'newspaper',
  'live-events': 'video',
  media: 'image',
  games: 'trophy',
  world: 'globe',
  settings: 'settings',
};

/** The icon for a workspace id; Home's for anything unmapped, so a chip is never empty. */
export function workspaceNavIcon(id: string): string {
  return WORKSPACE_NAV_ICONS[id] ?? 'home';
}
